import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation, useQueryClient } from 'react-query'
import { Dayjs } from 'dayjs'
import { reportError } from 'utils/error'
import { usePollUntilPendingBrandPlanSchedules } from './usePollUntilPendingBrandPlanSchedules'
import { scheduleKeys } from './scheduleKeys'

export type ContentType =
  | 'instagram_post'
  | 'instagram_reel'
  | 'instagram_story'
  | 'tiktok_post'
  | 'youtube_short'
  | 'facebook_post'
  | 'linkedin_post'
  | 'twitter_post'
  | 'twitter_thread'
  | 'newsletter'
  | 'blog_post'
  | 'crosspost_post'
  | 'video_crosspost_post'

export interface Schedule {
  type: ContentType
  topic: string
  post_at: Dayjs
  initial_media_file_ids?: string[]
}

type Data = {}

const addToBrandPlanSchedule = async ({
  workspaceId,
  brandPlanId,
  brandPlanScheduleId,
  schedule,
  signal,
}: {
  workspaceId: number
  brandPlanId: number
  brandPlanScheduleId: number
  schedule: Schedule[]
  signal?: AbortSignal
}) => {
  const res = await sendToApi(
    `/w/${workspaceId}/brand_plans/${brandPlanId}/schedules/${brandPlanScheduleId}/add_more`,
    {
      method: 'POST',
      body: {
        contents: schedule.map((item) => ({
          ...item,
          post_at: item.post_at.toISOString(),
        })),
      },
      signal,
    },
  )
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data
}

export const useAddToBrandPlanSchedule = () => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const pollUntilPendingSchedules = usePollUntilPendingBrandPlanSchedules({})

  return useMutation<Data, Error, { schedule: Schedule[]; brandPlanId: number; brandPlanScheduleId: number }>(
    ({ schedule, brandPlanId, brandPlanScheduleId }) =>
      addToBrandPlanSchedule({ workspaceId, schedule, brandPlanId, brandPlanScheduleId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(scheduleKeys.pending(workspaceId))
        pollUntilPendingSchedules()
      },
      onError: (error) => {
        reportError(error)
      },
    },
  )
}
